import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AuditLog, AuditLogQuery } from '@/types'
import { auditLogsApi } from '@/lib/api'

export const useAuditLogsStore = defineStore('auditLogs', () => {
  const logs = ref<AuditLog[]>([])
  const selectedLog = ref<AuditLog | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref({
    page: 1,
    pageSize: 20,
    total: 0,
    hasNext: false,
  })

  // Current filters
  const filters = ref<AuditLogQuery>({
    page: 1,
    pageSize: 20,
  })

  const hasLogs = computed(() => logs.value.length > 0)

  const fetchLogs = async (query?: AuditLogQuery) => {
    loading.value = true
    error.value = null

    try {
      // Merge with current filters
      const params = { ...filters.value, ...query }
      filters.value = params

      const response = await auditLogsApi.list(params)
      logs.value = response.data
      meta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch audit logs'
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchLog = async (id: string) => {
    loading.value = true
    error.value = null

    try {
      const response = await auditLogsApi.get(id)
      selectedLog.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch audit log'
      throw err
    } finally {
      loading.value = false
    }
  }

  const setPage = (page: number) => {
    fetchLogs({ ...filters.value, page })
  }

  const setPageSize = (pageSize: number) => {
    fetchLogs({ ...filters.value, page: 1, pageSize })
  }

  const setFilters = (newFilters: Partial<AuditLogQuery>) => {
    fetchLogs({ ...filters.value, ...newFilters, page: 1 })
  }

  const clearFilters = () => {
    filters.value = { page: 1, pageSize: 20 }
    fetchLogs(filters.value)
  }

  const clearSelectedLog = () => {
    selectedLog.value = null
  }

  return {
    logs,
    selectedLog,
    loading,
    error,
    meta,
    filters,
    hasLogs,
    fetchLogs,
    fetchLog,
    setPage,
    setPageSize,
    setFilters,
    clearFilters,
    clearSelectedLog,
  }
})
